// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Implement adhesive product.
 * @author James Reid
 */

// @ts-check

// @@imports-module
import { Product } from "./Product.js"

// @@imports-types
import {
    ProductData,
    UNITS,
    MEMBRANE_TYPES,
    ADHESIVE_TYPES,
    ADHESIVE_CATEGORIES,
    PRODUCT_DISPLAY_CATEGORIES,
    ADHESIVE_APPLICATIONS,
} from "../types/index.js"

// @@body
class Adhesive extends Product {
    /**
     * Construct adhesive product from data returned from server.
     * @param {ProductData} data Product data returned from server.
     * @param {any} match Regexp match data for internal calculator sku code
     * pertaining to this extended class.
     */
    constructor(data, match) {
        super(data, { unit: UNITS.volume.litre })

        // Parse regexp data.
        const { type, membrane, application, volume } =
            /** @type {Object<string, string>} */ (match.groups)

        // Set extended class values.
        this.type = type === "W" ? ADHESIVE_TYPES.deck.waterBased
            : type === "C" ? ADHESIVE_TYPES.deck.contactBonding
            : type === "U" ? ADHESIVE_TYPES.deck.pu
            : type === "P" ? ADHESIVE_TYPES.primer
            : type === "L" ? ADHESIVE_TYPES.sealants.lapEdge
            : type === "B" ? ADHESIVE_TYPES.sealants.waterBlock
            : type === "M" ? ADHESIVE_TYPES.sealants.msPolymer
            : ADHESIVE_TYPES.sealants.blackSilicone
        this.membraneType = membrane === "S" ? MEMBRANE_TYPES.onePiece
            : membrane === "F" ? MEMBRANE_TYPES.fleeceBacked
            : null
        this.category = type.match(/^[WCU]$/) ? ADHESIVE_CATEGORIES.deck
            : type === "P" ? ADHESIVE_CATEGORIES.primer
            : ADHESIVE_CATEGORIES.sealants
        this.displayCategory = this.category === ADHESIVE_CATEGORIES.deck
                ? PRODUCT_DISPLAY_CATEGORIES.deckAdhesive
            : this.category === ADHESIVE_CATEGORIES.primer
                ? PRODUCT_DISPLAY_CATEGORIES.primer
            : PRODUCT_DISPLAY_CATEGORIES.sealant
        this.application = application === "R" ? ADHESIVE_APPLICATIONS.roller
            : application === "A" ? ADHESIVE_APPLICATIONS.aerosol
            : application === "S" ? ADHESIVE_APPLICATIONS.spray
            : ADHESIVE_APPLICATIONS.sealantGun

        // Infer exact dimensions required for calculations from calculator sku.
        this.quantity = parseInt(volume) / 100
    }
}

// @@exports
export { Adhesive }
